import { skipToken, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CEX_QUERY_KEYS } from '../api.const';
import {
  buildSymbolStatusKey,
  fetchCexSymbolStatuses,
  type SymbolStatusMap,
  type SymbolStatusRequestItem,
} from '../symbolStatus';
import { DEFAULT_SYMBOL_STATUS_TTL_MS } from '../symbolStatusCacheStorage';

type Params = {
  items?: SymbolStatusRequestItem[];
  enabled?: boolean;
};

const normalizeItems = (items: SymbolStatusRequestItem[] = []): SymbolStatusRequestItem[] =>
  items
    .filter(item => item.cex && item.base && item.quote)
    .map(item => ({
      cex: item.cex,
      base: item.base.toUpperCase(),
      quote: item.quote.toUpperCase(),
    }));

export const useCexSymbolStatuses = ({ items = [], enabled = true }: Params = {}) => {
  const normalized = useMemo(() => normalizeItems(items), [items]);
  const keys = useMemo(
    () =>
      Array.from(
        new Set(normalized.map(item => buildSymbolStatusKey(item.cex, item.quote, item.base))),
      ).sort(),
    [normalized],
  );
  const shouldFetch = enabled && normalized.length > 0;

  return useQuery<SymbolStatusMap>({
    queryKey: CEX_QUERY_KEYS.symbolStatuses(keys),
    queryFn: shouldFetch ? () => fetchCexSymbolStatuses(normalized) : skipToken,
    enabled: shouldFetch,
    staleTime: DEFAULT_SYMBOL_STATUS_TTL_MS,
    refetchOnWindowFocus: false,
    gcTime: 30 * 60 * 1000,
  });
};
